import React, { useMemo } from 'react'
import { Marked } from 'marked'

import { css } from 'src/ui/style/css'
import { useTheme } from 'src/ui/style/useTheme'

export type MarkdownTextProps = {
    content: string
    openLinksInNewTab?: boolean
    color?: string
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({
    content,
    openLinksInNewTab,
    color
}) => {
    const th = useTheme()

    // Create a local Marked instance (avoids mutating global state)
    const markedInstance = useMemo(() => {
        return new Marked({
            breaks: true,
            gfm: true,
            ...(openLinksInNewTab
                ? {
                      renderer: {
                          link({ href, title, text }) {
                              const titleAttr = title
                                  ? ` title="${title}"`
                                  : ''
                              return `<a href="${href}" ${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
                          }
                      }
                  }
                : {})
        })
    }, [openLinksInNewTab])

    const htmlContent = useMemo(() => {
        return markedInstance.parse(content) as string
    }, [content, markedInstance])

    return (
        <div
            css={css`
                color: ${color || th.colors.black} !important;

                // Paragraphs

                p {
                    color: inherit !important;
                    margin-bottom: ${th.spacing.md};

                    &:last-child {
                        margin-bottom: 0;
                    }
                }

                // Links

                a {
                    color: ${color === th.colors.white
                        ? th.colors.secondary
                        : th.colors.primary} !important;
                    text-decoration: none;
                    border-bottom: 1px solid transparent;
                    transition: border-color 0.2s ease;

                    &:hover {
                        border-bottom-color: ${color === th.colors.white
                            ? th.colors.secondary
                            : th.colors.primary};
                    }
                }

                // Lists

                ul,
                ol {
                    margin-bottom: ${th.spacing.md};
                    padding-left: ${th.spacing.lg};
                }

                li {
                    margin-bottom: ${th.spacing.sm};
                }

                // Headers

                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    margin-top: ${th.spacing.lg};
                    margin-bottom: ${th.spacing.md};
                    font-weight: 600;
                }

                // Code

                code {
                    background-color: ${th.colors.secondary || '#f5f5f5'};
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-family: monospace;
                    font-size: 0.9em;
                }

                pre {
                    background-color: ${th.colors.secondary || '#f5f5f5'};
                    padding: ${th.spacing.md};
                    border-radius: 6px;
                    overflow-x: auto;
                    margin-bottom: ${th.spacing.md};

                    code {
                        background: none;
                        padding: 0;
                    }
                }
            `}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    )
}
